<template>
  <label :class="$style.option">
    <SCACheckboxBase :checked="boxChecked" :deselect="indeterminate">
      <input type="checkbox" :test-id="testId" :checked="boxChecked" @change="onChange" />
    </SCACheckboxBase>
    <SCATag
      v-if="color"
      :class="$style.tag"
      :style="{ color: color.textColor, backgroundColor: color.bgColor }"
      tabindex="-1"
    >
      <span ref="textRef" :class="$style.text">
        <SCATextHighlighter :text="label" :search-term="searchTerm" />
      </span>
    </SCATag>
    <span v-else ref="textRef" :class="$style.text">
      <SCATextHighlighter :text="label" :search-term="searchTerm" />
    </span>
    <SCATooltip v-if="isTruncated" :target="textRef" :text="label" />
  </label>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import SCACheckboxBase from "../../SCACheckboxBase.vue";
import SCATag from "../../SCATag.vue";
import SCATextHighlighter from "../../SCATextHighlighter.vue";
import SCATooltip from "../../SCATooltip.vue";
import { useIsTruncated } from "../../useIsTruncated";
import type { ListOptionColor } from "../types";

const props = withDefaults(
  defineProps<{
    label: string;
    checked: boolean;
    indeterminate?: boolean;
    searchTerm?: string;
    testId?: string;
    color?: ListOptionColor;
  }>(),
  {
    indeterminate: false,
    searchTerm: "",
    testId: undefined,
    color: undefined,
  },
);

const emit = defineEmits<{
  change: [checked: boolean];
}>();

const boxChecked = computed(() => props.checked || props.indeterminate);

function onChange(event: Event) {
  const input = event.target as HTMLInputElement;
  emit("change", input.checked);
  input.checked = boxChecked.value;
}

const textRef = ref<HTMLElement | null>(null);
const isTruncated = useIsTruncated(textRef, () => props.label);
</script>

<style module lang="scss">
@use "../../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.option {
  @include FigmaTypoMixins.typo-body-medium;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: var(--space-component-gap-small);
  height: var(--size-xSmall);
  padding: 0 6px;
  color: var(--color-content-primary);
  cursor: pointer;
}

.option .tag {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  padding-top: var(--space-component-padding-2xSmall);
  padding-bottom: var(--space-component-padding-2xSmall);
  padding-left: var(--space-component-padding-xSmall);
}

.text {
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
